import { useCallback, useEffect, useState } from "react"

import ConfirmationModal, {
  ModalInfo,
} from "components/Modals/ConfirmationModal"
import { Tab, Tabs, styledTabClasses, styledTabsProps } from "components/Tabs"
import { css, cx } from "@emotion/css"
import { deleteTeam, fetchClientConfigTeams, fetchTeam } from "api/team"
// import Members from "./Members"
import { queryCache, useMutation, useQuery } from "react-query"

import { AgencyClientTeamLeads } from "./AgencyClientTeamLeads"
import { Box } from "@material-ui/core"
import DeleteIcon from "@material-ui/icons/Cancel"
import { EmptyState } from "components/EmptyState"
import { Loading } from "components/Loading"
import Members from "../../AgencyTeams/Members"
import { TeamMember } from "models/common"
import { ReactComponent as TeamsIcon } from "assets/icon-team.svg"
import { fetchClientTeamLeads } from "api/clients"
import { useAuthenticated } from "providers/A12n"
import { useI18n } from "providers/I18n"
import { useParams } from "react-router-dom"

export function ConfigCLientTeams() {
  const { t } = useI18n()
  const { configId } = useParams<{ configId: string }>()
  const [modalInfo, setModalInfo] = useState<ModalInfo>(undefined)
  const [tab, setTab] = useState<string>()
  const [roleTab, setRoleTab] = useState(() => "planning")
  const { hasAuthority } = useAuthenticated()

  const {
    data: teams,
    isLoading,
    updatedAt,
  } = useQuery(["agency-teams", configId], fetchClientConfigTeams)

  const { data: team, isLoading: isTeamLoading } = useQuery(
    ["agency-team", tab],
    fetchTeam,
    {
      enabled: !!tab,
    },
  )
  const noData = !teams?.length

  const { data: teamLeads } = useQuery(
    ["client-team-leads", configId],
    fetchClientTeamLeads,
  )

  const teamMembers = team?.users?.map((tu) => ({
    name: [tu.firstName, tu.lastName].join(" "),
    ...tu,
  })) as Array<TeamMember & { name: string }>

  const onTeamSelect = useCallback((teamId?: string) => {
    !!teamId && setTab(teamId)
  }, [])

  const [delTeam] = useMutation(deleteTeam, {
    onSuccess: () => {
      setModalInfo(undefined)
      queryCache.invalidateQueries("agency-teams")
      !!teams?.length && setTab(teams[0].id)
    },
    onError: () => setModalInfo(undefined),
  })
  useEffect(() => {
    !!teams?.length && !tab && setTab(teams[0].id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updatedAt])

  const onDelete = useCallback(
    (teamId: string) => {
      setModalInfo({
        title: t("teams.list.delete.title"),
        type: "primary_destructive",
        description: t("teams.list.delete.description"),
        submitLabel: t("teams.list.delete.action"),
        onSubmit: async () => await delTeam(teamId),
      })
    },
    [delTeam, t],
  )

  const isModalOpen = Boolean(modalInfo)
  const noTeamLead = !teamLeads?.length

  return (
    <>
      {isLoading ? (
        <Loading />
      ) : (
        <Box className={styles.dataWrapper}>
          <span className="BodyBody-1BlackBold">
            {t("agency.client.team.leads")}
          </span>
          <div className={cx("BodyBody-1BlackRegular", styles.inputWrapped)}>
            {noTeamLead && !hasAuthority(["AUTH_EDIT_TEAM"]) ? (
              t("team.lead.empty")
            ) : (
              <div className={styles.box}>
                <Tabs
                  value={roleTab}
                  onTabChange={setRoleTab}
                  TabIndicatorProps={{
                    style: {
                      display: "none",
                    },
                  }}
                  classes={{ flexContainer: styles.roleTabs }}
                >
                  <Tab
                    label={t("agency.client.team.leader.planning")}
                    value="planning"
                    classes={styledTabClasses}
                    className={styles.roleTab}
                  />
                  <Tab
                    label={t("agency.client.team.leader.buying")}
                    value="buying"
                    classes={styledTabClasses}
                    className={styles.roleTab}
                  />
                </Tabs>
                <div className={styles.roleWrapper}>
                  {roleTab === "planning" ? (
                    <AgencyClientTeamLeads
                      teamLeads={teamLeads}
                      role="PLANNER"
                    />
                  ) : null}
                  {roleTab === "buying" ? (
                    <AgencyClientTeamLeads teamLeads={teamLeads} role="BUYER" />
                  ) : null}
                </div>
              </div>
            )}
          </div>
          <div className={styles.main}>
            {!!tab ? (
              <Tabs
                variant="scrollable"
                scrollButtons="auto"
                value={tab}
                onTabChange={onTeamSelect}
                {...styledTabsProps}
              >
                {teams?.map((team, idx) => (
                  <Tab
                    key={idx}
                    classes={styledTabClasses}
                    className={styles.tab}
                    value={team.id}
                    label={
                      <span className={styles.tabLabel}>
                        <span className={styles.label}>{team.name}</span>
                        {hasAuthority(["AUTH_DELETE_TEAM"]) && (
                          <DeleteIcon
                            onClick={(e) => onDelete(team.id)}
                            className={styles.icon}
                          />
                        )}
                      </span>
                    }
                    selected={tab === team.id}
                  />
                ))}
              </Tabs>
            ) : null}
            {!!tab ? (
              <div
                className={cx(styles.wrapper, {
                  [styles.noDataWrapper]: noData,
                })}
              >
                <Members
                  isLoading={isTeamLoading}
                  teamId={tab}
                  data={teamMembers}
                />
              </div>
            ) : null}
            {!team && !tab ? (
              <EmptyState
                title={t("agencies.team.emptyState.title")}
                className={styles.emptyStateWidth}
                icon={<TeamsIcon />}
              />
            ) : null}
          </div>
        </Box>
      )}
      <ConfirmationModal
        open={isModalOpen}
        onCancel={() => setModalInfo(undefined)}
        {...modalInfo}
      />
    </>
  )
}

const styles = {
  box: css`
    display: flex;
    flex-direction: column;
    width: 540px;
    margin: 0px 524px 0px 0px;
  `,
  dataWrapper: css`
    padding: var(--spacing-xxl-2) 0px;
  `,
  roleTabs: css`
    border-bottom: 1px solid var(--light-grey);
    height: 32px;
    min-width: fit-content;
  `,
  roleTab: css`
    min-height: var(--spacing-xxl-2);
    &.MuiButtonBase-root {
      background-color: var(--light-grey-2);
      padding: var(--spacing-xs) var(--spacing-m);
      min-width: 0;
      border: solid 1px var(--light-grey);
      border-top-right-radius: 4px;
      border-top-left-radius: 4px;
      border-bottom: solid 1px var(--light-grey);
      span {
        font-size: 13px;
        color: var(--grey);
        text-transform: none;
      }
    }
    &.MuiButtonBase-root.Mui-selected {
      background-color: var(--white);
      border-bottom: solid 1px var(--white);
      span {
        color: var(--black);
        font-weight: normal;
      }
    }
  `,
  roleWrapper: css`
    display: flex;
    flex-direction: column;
    padding: var(--spacing-xxl-2) var(--spacing-xl);
    border-radius: 3px;
    box-shadow: 0 0 0 0 rgba(0, 0, 0, 0.16);
    border-style: solid;
    border-color: var(--light-grey);
    border-width: 0 1px 1px 1px;
    margin-top: -16px;
    min-height: 248px;
  `,
  main: css`
    flex: 1;
    display: flex;
    flex-direction: column;
    padding-top: var(--spacing-xxl-6);
  `,
  noDataMain: css`
    flex: 1;
    display: flex;
    flex-direction: column;
    margin-top: 0px;
  `,
  tab: css`
    height: 100%;
  `,
  tabLabel: css`
    display: flex;
    align-items: center;
    justify-content: space-around;
    width: 100%;
  `,
  label: css`
    min-width: 80px;
  `,
  wrapper: css`
    margin-top: var(--spacing-xxl-2);
  `,
  inputRow: css`
    width: 125px;
    height: 32px;
    padding: 4px 7.3px 4px 31px;
  `,
  icon: css`
    object-fit: contain;
    color: var(--disabled-grey);
  `,
  emptyStateWidth: css`
    width: 100%;
  `,
  noDataWrapper: css`
    margin-top: 0px;
    padding-top: var(--spacing-m);
  `,
  inputWrapped: css`
    display: flex;
    padding-top: var(--spacing-xxl-2);
    > span:nth-child(even) {
      margin-right: var(--spacing-xxs);
    }
    > span:last-child {
      display: none;
    }
  `,
}
